import * as React from "react";
import { FontIcon, Stack } from "@fluentui/react";
import { buildTestId } from "../../helpers/testIdHelper";
import { uniqueGUID } from "../../helpers/util";
import { BaseInputProps } from "./BaseInput";
import {
  FormContext,
  FormGroupValidationModeContext,
  FormInputGroupLayout,
  FormInputGroupLayoutContext,
  FormInputGroupLayoutContextProvider,
  FormState,
  FormValidationMode,
  FormValidationModeContext,
  FormValues,
  ValidationState,
} from "./Form";
import { renderErrorMessages, SimpleBuilderInternalProps } from "./InputCommon";

export interface SimpleBuilderState {
  /**
   * Current row index
   */
  currentRowNdx: number;
}

export const SimpleBuilderContext = React.createContext<SimpleBuilderState>({} as SimpleBuilderState);
export const SimpleBuilderContextConsumer = SimpleBuilderContext.Consumer;
const SimpleBuilderContextProvider = SimpleBuilderContext.Provider;

type TextBaseProps = Pick<BaseInputProps, "fieldName">;

export interface SimpleBuilderProps extends SimpleBuilderInternalProps, TextBaseProps {
  /**
   * A builder row. Will be rendered once per row in the current builder state. Typically just a mix of inputs
   * For full compatibility, any wrapped inputs should expose certain props:
   *  - fieldName
   *  - label
   *  - tooltip?
   *  - defaultValue?
   *  - validator?
   *  - onChange?
   *  - disabled?
   *  - readOnly?
   */
  children: JSX.Element | JSX.Element[];
  /**
   * Default number of rows
   * @default 1
   */
  defaultNumberOfRows?: number;
  /**
   * The maximum number of rows. Row addition will be disabled unless you have less rows than this
   */
  maxRows?: number;
  /**
   * The minimum number of rows. Row deletion will be disabled unless you have more rows than this
   * @default 1
   */
  minRows?: number;
  /**
   * One row will be rendered per value in the array, and the form state will be initialized to the values provided.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultValue?: { [key: string]: any }[];
  /**
   * Callback to validate the component
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validator?: (value: { [key: string]: any }[]) => string[] | undefined;
  /**
   * data-test-id HTML attribute value
   * will derive testIds for associated elements from this
   */
  testId?: string;
}

/**
 * This does not extend ComponentTestIds because there is no 'root' component
 */
export interface SimpleBuilderTestIds {
  error: string;
  deleteRow: string;
}

export const buildSimpleBuilderTestIds = (testId?: string): SimpleBuilderTestIds => ({
  error: buildTestId(testId, "-error"),
  deleteRow: buildTestId(testId, "-delete-row"),
});

/**
 * The SimpleBuilder component render rows (children components) along with a remove button.
 * The component has optional props for controlling the minimum and maximum number of rows allowed,
 * and disables controls when those constraints would be violated.
 */
export const SimpleBuilder = ({
  label,
  children,
  defaultNumberOfRows = 1,
  fieldName,
  minRows = 1,
  maxRows,
  defaultValue,
  validator,
  testId,
}: SimpleBuilderProps): JSX.Element => {
  if (maxRows && maxRows < minRows) {
    throw new Error("maxRows should be greater than or equal to minRows.");
  }

  if (defaultNumberOfRows) {
    if (defaultNumberOfRows < minRows) {
      throw new Error("defaultNumberOfRows must be greater than or equal to minRows.");
    }

    if (maxRows && defaultNumberOfRows > maxRows) {
      throw new Error("defaultNumberOfRows must be less than or equal to maxRows.");
    }
  }

  const form: FormState = React.useContext(FormContext);
  const { groupName: layoutGroup } = React.useContext(
    FormInputGroupLayoutContext,
  );

  const fieldNameSet = new Set<string>();
  let rowCount = (defaultValue && defaultValue.length + 1) || defaultNumberOfRows;
  if (rowCount && maxRows && rowCount > maxRows) {
    rowCount = maxRows;
  }
  const [rows, setRows] = React.useState<number[]>([...Array(rowCount).keys()]); // array of [rowIndex, keyIndex]
  const [nextIndex, setNextIndex] = React.useState<number>(rowCount); // always increment to generate unique keys
  const [simpleBuilderState, setSimpleBuilderState] = React.useState<SimpleBuilderState>({ currentRowNdx: -1 });

  if (!Object.keys(form).length) {
    throw new Error("SimpleBuilder should be used within form");
  }

  const [errorMessage, setErrorMessage] = React.useState<string[]>([]);
  const allErrors = [...errorMessage || []];
  const validationMode = React.useContext(FormValidationModeContext);
  const groupValidationMode = React.useContext(FormGroupValidationModeContext);

  const simpleBuilderTestIds = buildSimpleBuilderTestIds(testId);

  React.useEffect(() => {
    if (label) {
      form.registerFieldLabel(fieldName, label);
      form.registerFieldGroup(fieldName, layoutGroup);
    }
    if (validator) {
      form.registerField(fieldName, layoutGroup);
    }
    return function cleanup() {
      if (validator) {
        form.unRegisterField(fieldName, layoutGroup);
      }
    };
    // Only at mount time. Others props should not have side effect beyond their initial values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateBuilderState = (): void => {
    const errorMsg: string[] = [];
    if (validator) {
      const validatorErrors = validator(form.getValue(fieldName, layoutGroup)) || [];
      errorMsg.push(...validatorErrors);
    }
    form.setFieldState(
      errorMsg && errorMsg.length > 0 ? ValidationState.INVALID : ValidationState.VALID,
      fieldName,
      layoutGroup,
    );
    setErrorMessage(errorMsg);
  };

  React.useEffect(() => {
    if ((validationMode === FormValidationMode.ON || groupValidationMode[layoutGroup] === FormValidationMode.ON)) {
      validateBuilderState();
    }
    // Only if validation mode changes. Others props should not have side effect beyond their initial values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationMode, groupValidationMode]);

  React.useEffect(() => {
    transformData();
    // Only at mount time or form contents change, transform defaultValues or empty values into value array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(form)]);

  const transformData = (): void => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const map = new Map<string, any>();
    const formValues: FormValues = form.getGroupValues(layoutGroup);

    Object.keys(formValues || {}).forEach(value => {
      if (value === fieldName) return;

      const [propName, id] = value.split("-");
      if (propName && id) {
        const existingProps = map.get(id) || {};
        if (formValues[value]) {
          map.set(id, { [propName]: formValues[value], ...existingProps });
        }
      }
    });

    const transformedData = Array.from(map.values());
    form.setValue(transformedData, fieldName, layoutGroup);
  };

  const addRow = (): void => {
    setRows([...rows, nextIndex]);
    setNextIndex(nextIndex + 1);
  };

  const removeRow = (id: number): void => {
    const newRows = rows.filter(rowId => rowId !== id);

    const simpleBuilderValues = form.getValue(
      fieldName,
      layoutGroup,
    ) as Array<Record<string, unknown>> | undefined;

    // Account for inputs that are disabled or readOnly
    const filledRows = simpleBuilderValues?.filter(value => {
      if (Array.isArray(children)) {
        const hasValue: boolean = children.reduce((valueFound, child) => {
          const { disabled, readOnly, fieldName: childFieldName } = child.props;
          // If the input is not disabled
          // And it is not readOnly
          // and has a value
          // Then it is valid
          return (!disabled && !readOnly && !!value[childFieldName]) || valueFound;
        }, false as boolean);

        return hasValue;
      }
      // If the child is not disabled and not readonly
      // Then it is a valid entry
      const { disabled, readOnly } = children.props;
      return !disabled && !readOnly;
    });

    // Check that all rows have a value
    const allEntriesFilled = filledRows?.length === rows.length;
    // Check that the newRows have space for exactly 1 more row
    const hasSpaceForNewRow = maxRows && maxRows - 1 === newRows.length;

    if (hasSpaceForNewRow && allEntriesFilled) {
      setRows([...newRows, nextIndex]);
      setNextIndex(nextIndex + 1);
    } else {
      setRows(newRows);
    }

    fieldNameSet.forEach(name => {
      form.unRegisterField(`${name}-${id}`, layoutGroup);
      form.deleteField(`${name}-${id}`, layoutGroup);
    });
    transformData();
  };

  const isFieldTouched = (fieldNameVal: string): boolean => {
    const val = form.getValue(fieldNameVal, layoutGroup);
    return val;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rowOnChange = (rowIndex: number, fieldOnchange: any): void => {
    if (rowIndex === rows.length - 1) {
      if (maxRows) {
        if (maxRows > rows.length) {
          addRow();
        }
      } else {
        addRow();
      }
    }
    fieldOnchange?.();
    transformData();
    setSimpleBuilderState({ currentRowNdx: rowIndex });
    validateBuilderState();
  };

  const getRowCopy = (child: JSX.Element, id: number, rowIndex: number): JSX.Element => {
    const {
      testId: fieldTestId,
      fieldName: name,
      label: fieldLabel,
      tooltip: fieldTooltip,
      defaultValue: fieldDefaultValue,
      validator: fieldValidator,
      onChange: fieldOnchange,
    } = child.props;
    const isInitialPosition = id === rowIndex;
    const rowFieldName = name ? `${name}-${id}` : "";
    const rowKey = rowFieldName || uniqueGUID();
    const rowDefaultValue = (
      defaultValue && isInitialPosition ? defaultValue?.[rowIndex]?.[name] : undefined
        || fieldDefaultValue
    );
    const { fLabel, tooltip } = rowIndex === 0
      ? { fLabel: fieldLabel, tooltip: fieldTooltip } : { fLabel: undefined, tooltip: undefined };

    // Shift `:` separator in first row - verify if there's no fieldName and is 1st row
    const nonInputElement = rowIndex === 0 && !name
      ? { style: { paddingTop: "30px", paddingLeft: "8px", paddingRight: "8px", paddingBottom: "8px" } }
      : { style: { paddingTop: "4px", paddingLeft: "8px", paddingRight: "8px" } };

    const validatorWrapper = (value: string): string[] | undefined => {
      if (isFieldTouched(rowFieldName)) {
        return fieldValidator(value);
      }
      return undefined;
    };

    // Create row copy and
    const rowCopy = React.cloneElement(child, {
      key: rowKey,
      ...child.props,
      ...(name ? {
        testId: fieldTestId,
        label: fLabel,
        tooltip,
        fieldName: rowFieldName,
        defaultValue: rowDefaultValue,
        onChange: () => rowOnChange(rowIndex, fieldOnchange),
      } : {}),
      ...(fieldValidator ? { validator: validatorWrapper } : {}),
      ...(nonInputElement),
    });

    fieldNameSet.add(name);

    return (
      <FormInputGroupLayoutContextProvider value={{ groupName: layoutGroup, layout: FormInputGroupLayout.COMPACT }}>
        {rowCopy}
      </FormInputGroupLayoutContextProvider>
    );
  };

  const getRow = (id: number, rowIndex: number): JSX.Element | JSX.Element[] => (
    (children as JSX.Element[])?.length
      ? React.Children.map(children, child => getRowCopy(child, id, rowIndex))
      : getRowCopy(children as JSX.Element, id, rowIndex)
  );

  const renderRow = (rowId: number, rowIndex: number): JSX.Element => {
    const onRowRemove = (): void => {
      removeRow(rowId);
      validateBuilderState();
    };
    const deleteIconStyle = {
      cursor: "pointer",
      color: "#0078d4",
      padding: rowIndex === 0 ? "33px 8px 12px 8px" : "8px 8px 2px 8px",
    };
    const showDelete = rows.length > minRows && (rowIndex !== (rows.length - 1));
    return (
      <Stack key={`row-${rowId}`} horizontal>
        {getRow(rowId, rowIndex)}
        {showDelete
          ? (
            <FontIcon
              style={deleteIconStyle}
              iconName="Delete"
              onClick={onRowRemove}
              data-test-id={simpleBuilderTestIds.deleteRow}
            />
          )
          : (
            <div style={{ paddingLeft: "15px", paddingRight: "15px" }} />
          )}
      </Stack>
    );
  };
  return (
    <SimpleBuilderContextProvider value={simpleBuilderState}>
      {rows.map((rowId, rowIndex) => renderRow(rowId, rowIndex))}
      {allErrors.length > 0 && renderErrorMessages(allErrors, FormInputGroupLayout.COMPACT, simpleBuilderTestIds.error)}
    </SimpleBuilderContextProvider>
  );
};
